import { mascaraCep, mascaraTelefone } from '@/nucleo/formato/documentos';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import { ROTULO_DO_REGIME_TRIBUTARIO, type Credenciado } from './contrato';

interface AbaDadosGeraisProps {
  credenciado: Credenciado;
}

/**
 * Aba "Dados Gerais" — contato, endereço, observações e dados bancários, como a API
 * devolve no detalhe (`credenciados_operacao_detalhe`, 0360). Os contatos chegam
 * inteiros porque é para isso que a aba existe; o banco registra a leitura em
 * `acessos_dado_pessoal`. Campo vazio no cadastro aparece como "—", nunca inventado.
 */
export function AbaDadosGerais({ credenciado }: AbaDadosGeraisProps) {
  const endereco = [credenciado.endereco, credenciado.numero, credenciado.complemento]
    .map((parte) => parte?.trim())
    .filter(Boolean)
    .join(', ');
  const cidade = [credenciado.cidade?.trim(), credenciado.uf?.trim()].filter(Boolean).join('/');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Informações de contato</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <dl className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="E-mail" valor={credenciado.email} quebra />
          <Campo
            rotulo="Telefone"
            valor={credenciado.telefone ? mascaraTelefone(credenciado.telefone) : null}
            mono
          />
          <Campo
            rotulo="Telefone 2"
            valor={credenciado.telefone2 ? mascaraTelefone(credenciado.telefone2) : null}
            mono
          />
          <Campo rotulo="Termo Permissionário (TP)" valor={credenciado.termo_permissionario} />
          <Campo rotulo="Endereço" valor={endereco || null} />
          <Campo rotulo="Bairro" valor={credenciado.bairro} />
          <Campo rotulo="Cidade/UF" valor={cidade || null} />
          <Campo rotulo="CEP" valor={credenciado.cep ? mascaraCep(credenciado.cep) : null} mono />
          {credenciado.tipo !== 'PF' && (
            <Campo
              rotulo="Regime tributário"
              valor={
                credenciado.regime_tributario
                  ? ROTULO_DO_REGIME_TRIBUTARIO[credenciado.regime_tributario]
                  : null
              }
            />
          )}
        </dl>

        {credenciado.observacoes && (
          <dl className="border-t pt-4">
            <dt className="text-muted-foreground mb-1 text-xs">Observações</dt>
            <dd className="text-sm whitespace-pre-wrap">{credenciado.observacoes}</dd>
          </dl>
        )}

        <div className="border-t pt-4">
          <h4 className="mb-3 text-sm font-semibold">Dados bancários</h4>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Banco" valor={credenciado.conta_banco} />
            <Campo rotulo="Agência" valor={credenciado.conta_agencia} mono />
            <Campo rotulo="Conta" valor={credenciado.conta_numero} mono />
            <Campo rotulo="Operação" valor={credenciado.conta_operacao} />
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}

function Campo({
  rotulo,
  valor,
  mono,
  quebra,
}: {
  rotulo: string;
  valor: string | null;
  mono?: boolean;
  /** E-mail longo quebra em vez de empurrar a tela para o lado em 375 px. */
  quebra?: boolean;
}) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{rotulo}</dt>
      <dd className={[mono ? 'font-mono' : '', quebra ? 'break-all' : '', 'text-sm'].join(' ')}>
        {valor || '—'}
      </dd>
    </div>
  );
}
